import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import CreatableSelect from "react-select/creatable";
import * as Sentry from "@sentry/browser";
import { getApiUrl } from "@utils/baseUrl";
import { getSidebarCountData } from "@redux/Sidebar/sidebarCount.action";
import { toTitleCase } from "@containers/Hermes/utils/companyLinks";
import type { AppDispatch, ContactsOfferBlock, ContactsOfferCompany } from "@containers/Hermes/types";
import useHermesAnalytics from "@containers/Hermes/hooks/useHermesAnalytics";
import ActionCardShell, { SelectedCompaniesChip, resultsHeading } from "./ActionCardShell";

/**
 * The bookmark card, opened from a companies action bar's "Bookmark". ChatShell
 * renders it in place of the composer while it is open.
 *
 *   card (all companies selected, optional tags) ──▶ POST addFavouriteCompany
 *     └─ "N companies ⌄" ──▶ company picker ──▶ back to card
 *
 * Saves through the same endpoint and payload shape as company search's
 * AddFavModal, so a Hermes bookmark lands in the dashboard exactly like one
 * made anywhere else. Bookmarking is free, so unlike the download/contacts
 * actions there is no cap or cost confirmation.
 */

export type BookmarkCompany = {
  // The master company id — see toBookmarkCompanies.
  company_id: string;
  name: string;
  country?: string;
};

/**
 * The bookmarkable companies in a contacts offer.
 *
 * Bookmarks are saved against the master company id (company search's
 * `hashed_id`), not the alias `company_id` the profile links use — a bookmark
 * made with the alias id does not resolve in the dashboard. Companies without
 * one (messages persisted before the backend sent it) are left out. The list
 * also carries a match-only alias entry per company (same id, alternate name),
 * so de-duplicate on id.
 */
export function toBookmarkCompanies(offered: ContactsOfferCompany[]): BookmarkCompany[] {
  const seen = new Set<string>();
  const unique: BookmarkCompany[] = [];
  for (const company of offered) {
    const masterId = company.master_id;
    if (!masterId || seen.has(masterId)) continue;
    seen.add(masterId);
    unique.push({ company_id: masterId, name: toTitleCase(company.display || company.name), country: company.country });
  }
  return unique;
}

type TagOption = { label: string; value: string };

type BookmarkCardProps = {
  block: ContactsOfferBlock;
  onClose: () => void;
  modal?: boolean;
  // Records the answer's result holds, for the heading when opened from the bar beneath the table.
  totalRecords?: number;
  // Opens the full table to choose from every record (given only when the result is bigger than the preview).
  onCustomise?: () => void;
};

const MAX_TAGS = 10;

export default function BookmarkCard({ block, onClose, modal, onCustomise, totalRecords }: BookmarkCardProps) {
  const analytics = useHermesAnalytics();
  const dispatch = useDispatch<AppDispatch>();
  const companies = useMemo(() => toBookmarkCompanies(block.companies ?? []), [block.companies]);
  const pickerCompanies = useMemo(() => companies.map((company) => ({ key: company.company_id, name: company.name })), [companies]);

  // Mounted fresh on every open, so each open starts with every company
  // selected and no tags, per the design.
  const selectedIds = useMemo(() => new Set(companies.map((company) => company.company_id)), [companies]);
  const [tags, setTags] = useState<TagOption[]>([]);
  const [tagOptions, setTagOptions] = useState<TagOption[]>([]);
  const [busy, setBusy] = useState(false);

  // The user's existing tags, offered as suggestions in the tags field — same
  // source AddFavModal uses.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(getApiUrl("usersAllFavouriteCompanyTags"), { params: { type: "company" } })
      .then(({ data }) => {
        if (!cancelled && data?.favouriteCompanyTags?.length) setTagOptions(data.favouriteCompanyTags.map((tag: string) => ({ label: tag, value: tag })));
      })
      // Tag suggestions are a convenience — on failure the field still accepts new tags.
      .catch((error) => Sentry.captureException(error));
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = companies.filter((company) => selectedIds.has(company.company_id));
  const selectedCount = selected.length;

  const handleDiscard = () => {
    if (busy) return;
    onClose();
  };

  const handleBookmark = async () => {
    if (busy || selectedCount === 0) return;
    setBusy(true);
    analytics.bookmarkRequested({ companyCount: selectedCount, offeredCount: companies.length, tagCount: tags.length });

    try {
      const { data } = await axios.post(
        getApiUrl("addFavouriteCompany"),
        selected.map((company) => ({
          company_id: company.company_id,
          company_name: company.name,
          isAppendTags: true,
          is_merged: false,
          type: "company",
          company_user_pov: "",
          // Saved to intel_user_favourite_company.source, marking the bookmark as made from Hermes.
          source: "hermes",
          ...(company.country ? { company_country: company.country.toLowerCase() } : {}),
          ...(tags.length ? { tags: tags.map((tag) => tag.value) } : {}),
        }))
      );
      if (data?.status !== "success") throw new Error(data?.message || "addFavouriteCompany did not succeed");
      toast.success(data.message || `${selectedCount.toLocaleString()} companies added to bookmarks`);
      dispatch(getSidebarCountData());
      onClose();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Something went wrong. Please try again after sometime!!!");
      setBusy(false);
    }
  };

  return (
    <ActionCardShell modal={modal} title={modal ? "Bookmark" : resultsHeading(totalRecords ?? selectedCount)} primaryLabel="Bookmark" busyLabel="Bookmarking…" busy={busy} primaryDisabled={selectedCount === 0} onPrimary={handleBookmark} onDiscard={handleDiscard}>
      {/* A selection the user ticked in the table is not repeated back to them. */}
      {modal ? null : <SelectedCompaniesChip companies={pickerCompanies} selectedKeys={selectedIds} onCustomise={onCustomise} />}

      <p className="tw-m-0 tw-mt-4 tw-mb-2 tw-text-sm tw-font-medium tw-text-gray-900">Add Customised tags for easy access in the dashboard</p>
      <div className="tw-max-w-[458px]">
        <CreatableSelect isMulti isDisabled={busy} options={tagOptions} value={tags} onChange={(value) => setTags(((value as TagOption[]) || []).slice(0, MAX_TAGS))} isOptionDisabled={() => tags.length >= MAX_TAGS} placeholder={`You can add upto ${MAX_TAGS} tags`} formatCreateLabel={(input) => `Add tag "${input}"`} components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }} styles={tagSelectStyles} menuPlacement="top" />
      </div>
    </ActionCardShell>
  );
}

const tagSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 36,
    borderRadius: 4,
    borderColor: state.isFocused ? "#6F08F1" : "#EAECF0",
    boxShadow: "none",
    "&:hover": { borderColor: "#6F08F1" },
  }),
  placeholder: (base: any) => ({ ...base, fontSize: 14, color: "#98A2B3" }),
  multiValue: (base: any) => ({ ...base, borderRadius: 100, paddingLeft: 5, paddingRight: 5 }),
  menu: (base: any) => ({ ...base, zIndex: 1000 }),
};
